import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { EsignEnvelopesModal } from "@/pages/esign/widgets/EsignEnvelopesModal.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useEsignDocumentCounts, useEsignDocumentsSearch } from "@/queries/use-esign.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiEsignDocumentFilter,
  ApiEsignDocumentSummary,
  EsignEnvelopeStatus,
} from "@/types/esign.types.ts";
import {
  Box,
  Page,
  PaginationBlock,
  ProgressBar,
  TabItem,
  Table,
  TableCell,
  Tabs,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "react-use";

enum EsignRequestsTab {
  createdByMe,
  inProgress,
  completed,
  all,
}

const columnHelper = createColumnHelper<ApiEsignDocumentSummary>();

export default function EsignRequestsPage() {
  const { t } = useTranslation();
  const { renderRelativeDate } = useFormatter();
  const { openModal } = useModals();
  useTitle(t("core:esign_requests"), t("core:documents"));

  const handleOpen = useCallback(
    (document: ApiEsignDocumentSummary) => {
      openModal(EsignEnvelopesModal, {
        documentId: document.document_id,
        documentTitle: document.title,
        completedEnvelopesCount: document.completed_envelopes_count,
        activeEnvelopesCount: document.active_envelopes_count,
        totalEnvelopesCount: document.total_envelopes_count,
      });
    },
    [openModal]
  );

  const library = filtersLibrary();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    filter: { filter: pageFilter },
  } = useAppFilter<ApiEsignDocumentFilter>({
    filters: [library.page_number],
    source: "Esign requests",
  });
  const pageNumber = pageFilter.page_number ?? 1;
  const search = searchParams.get("search") ?? undefined;

  const [activeTab, setActiveTab] = useState(EsignRequestsTab.createdByMe);
  const [searchTerm, setSearchTerm] = useState(search ?? "");
  const { data: employeeMe } = useEmployeeMe();

  const resetPage = useCallback(() => {
    setSearchParams((searchParams) => {
      searchParams.delete("page");
      return searchParams;
    });
  }, [setSearchParams]);

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set("search", searchTerm);
        else searchParams.delete("search");
        searchParams.delete("page");
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const handleTabChange = (tab: EsignRequestsTab) => {
    setActiveTab(tab);
    resetPage();
  };

  const meReady = employeeMe !== undefined;

  const filterForTab = useCallback(
    (tab: EsignRequestsTab, page: number): ApiEsignDocumentFilter => {
      switch (tab) {
        case EsignRequestsTab.createdByMe:
          return { page_number: page, author_ids: employeeMe ? [employeeMe.id] : [] };
        case EsignRequestsTab.inProgress:
          return { page_number: page, status: EsignEnvelopeStatus.pending };
        case EsignRequestsTab.completed:
          return { page_number: page, status: EsignEnvelopeStatus.completed };
        case EsignRequestsTab.all:
          return { page_number: page };
      }
    },
    [employeeMe]
  );

  const filter = useMemo(
    () => ({ ...filterForTab(activeTab, pageNumber), search: search || undefined }),
    [filterForTab, activeTab, pageNumber, search]
  );

  const documentsQuery = useEsignDocumentsSearch(
    filter,
    activeTab !== EsignRequestsTab.createdByMe || meReady
  );

  const { data: counts } = useEsignDocumentCounts();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("datetime_last_sent_at", {
          header: t("common:date"),
          cell: (cell) => (
            <TableCell variant="secondary">{renderRelativeDate(cell.getValue(), false)}</TableCell>
          ),
          ...ColumnSizes.date,
        }),
        columnHelper.accessor("title", {
          header: t("core:document"),
          cell: (cell) => (
            <TableCell className="cursor-pointer hover:underline">{cell.getValue()}</TableCell>
          ),
          ...ColumnSizes.wide,
        }),
        columnHelper.display({
          id: "progress",
          header: t("common:progress"),
          cell: (cell) => {
            const document = cell.row.original;
            return (
              <div className="flex h-full min-h-8 w-35 items-center">
                <ProgressBar
                  completed={document.completed_envelopes_count}
                  total={document.active_envelopes_count}
                  variant="status"
                  className="w-full"
                  tooltip={t("core:esign_envelopes_completed_of", {
                    completed: document.completed_envelopes_count,
                    total: document.active_envelopes_count,
                  })}
                />
              </div>
            );
          },
          ...ColumnSizes.middle,
        }),
      ] as ColumnDef<ApiEsignDocumentSummary>[],
    [renderRelativeDate, t]
  );

  const tabItems = [
    { title: t("core:esign_created_by_me"), countTotal: counts?.created_by_me },
    { title: t("common:in_progress"), countTotal: counts?.in_progress },
    { title: t("common:status_completed"), countTotal: counts?.completed },
    { title: t("common:all"), countTotal: counts?.all },
  ];

  return (
    <Page className="h-full">
      <Page.Header title={t("core:esign_requests")} />
      <Page.Content stretch>
        <Page.StickyLayout
          toolbar={
            <>
              <Tabs activeTab={activeTab} onActiveTabChange={handleTabChange}>
                {tabItems.map(({ title, countTotal }) => (
                  <TabItem key={title} title={title} countTotal={countTotal} />
                ))}
              </Tabs>
              <Box className="mt-2">
                <Search
                  placeholder={t("common:search")}
                  value={searchTerm}
                  onInput={(e) => setSearchTerm(e.currentTarget.value)}
                  type="text"
                  clearable
                  onClear={() => setSearchTerm("")}
                />
              </Box>
            </>
          }
          footer={
            <PaginationBlock
              metadata={documentsQuery.data?.metadata}
              onPageChange={(page) => {
                setSearchParams((searchParams) => {
                  searchParams.set("page", page.toString());
                  return searchParams;
                });
              }}
            />
          }
        >
          <Table
            data={documentsQuery.data?.items ?? []}
            columns={columns}
            onRowClick={(row) => handleOpen(row.original)}
            stickyHeader={true}
            isFetching={documentsQuery.isFetching}
            showSkeleton={documentsQuery.isLoading}
            emptyStateComponent={<EmptyStateNothingFound />}
          />
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
}
